import React from 'react';
import { Link } from 'react-router-dom';
import LearnLayout from '../LearnLayout';
import { findTopic, neighbors } from '../topics';
import styles from '../Learn.module.css';

const ReadingSignals: React.FC = () => {
  const topic = findTopic('reading-signals')!;
  const { prev, next } = neighbors(topic.slug);
  return (
    <LearnLayout title={topic.title} tagline={topic.blurb} readTime={topic.readTime} prev={prev} next={next}>
      <p>
        One of the most common failure modes in discretionary trading is reading an indicator in isolation. RSI(2) = 5
        looks oversold. But oversold in what kind of day, at what time, in what market environment, on a name with what
        kind of short-side crowd? Those four overlays flip the trade.
      </p>

      <p>
        The SignalDeck Screener and Strategist both factor all four into every decision. Here's how to read them
        yourself so the output makes sense.
      </p>

      <h2>Layer 1 — The indicator (What)</h2>
      <p>
        This is the base signal. The <Link to="/glossary">Glossary</Link> covers each one. Fast to learn:
      </p>
      <ul>
        <li>
          <strong>RSI(2) &lt; 10</strong> — extreme oversold, reversal territory. Noisy; fires often; alone it's a coin
          flip.
        </li>
        <li>
          <strong>RSI(6) &lt; 20</strong> — deeper confirmation. Smoothes out RSI(2) noise.
        </li>
        <li>
          <strong>RSI(14) &lt; 30</strong> — traditional oversold; trend grade. When RSI(14) is ALSO oversold the setup
          has serious weight.
        </li>
        <li>
          <strong>Above VWAP</strong> — institutional buyers in control intraday. <strong>Below VWAP</strong> — the
          opposite.
        </li>
        <li>
          <strong>Volume rel. 20-day avg</strong> — confirmation. A move without volume is a fade candidate.
        </li>
      </ul>

      <h2>Layer 2 — The regime (Where we are)</h2>
      <p>Same RSI reading, different regime → totally different trade.</p>
      <div className={styles.example}>
        <div className={styles.label}>RSI(2) = 5 on AAPL</div>
        <ul>
          <li>
            <strong>Trend-up regime:</strong> healthy pullback in a rising market.{' '}
            <em>Buy the dip is the base case.</em>
          </li>
          <li>
            <strong>Chop regime:</strong> could be the bottom of a range, could be the start of a breakdown.{' '}
            <em>AVOID unless confluence is extreme.</em>
          </li>
          <li>
            <strong>Trend-down regime:</strong> oversold does not mean "about to bounce" — it means "the downtrend is
            strong." <em>Mean-reversion shorts are dangerous here; continuation trades are more honest.</em>
          </li>
        </ul>
      </div>

      <h2>Layer 3 — The session phase (When)</h2>
      <p>The clock matters more than most retail traders think.</p>
      <ul>
        <li>
          <strong>OPEN_RANGE (9:30-10:00 ET):</strong> highest volatility. First-five-minute extremes are noise.
          Range-break entries only become tradable after 10:00.
        </li>
        <li>
          <strong>MID_MORNING (10:00-11:30):</strong> cleanest tape. High-conviction window for trend continuation and
          mean-reversion bounces alike.
        </li>
        <li>
          <strong>LUNCH (11:30-14:00):</strong> lowest volume of the day. Fade breakouts, avoid new swing entries.
          <strong> This is the default-AVOID window.</strong>
        </li>
        <li>
          <strong>AFTERNOON (14:00-15:00):</strong> institutional desks wake up. Trend days resume direction, chop days
          stay choppy — volume confirms.
        </li>
        <li>
          <strong>POWER_HOUR (15:00-16:00):</strong> acceleration on trend days, fade-into-close on chop days. Avoid new
          swing entries after 15:30 — overnight risk you didn't sign up for.
        </li>
      </ul>

      <h2>Layer 4 — The short-side crowd (Who's opposite you)</h2>
      <p>Short interest shapes the asymmetry of any directional trade.</p>
      <div className={styles.example}>
        <div className={styles.label}>RSI oversold + …</div>
        <ul>
          <li>
            <strong>Short float &gt; 20% + days-to-cover &gt; 5:</strong> squeeze tailwind possible on any positive
            catalyst. Asymmetric longs are attractive; mean-reversion shorts are dangerous (you're joining a crowded
            trade late).
          </li>
          <li>
            <strong>Short float &lt; 3% + days-to-cover &lt; 2:</strong> clean float, no squeeze fuel. Shorts are viable
            as directional trades; mean-reversion is clean because there's no forced-covering pressure to trade against.
          </li>
          <li>
            <strong>Rising SI into a rally:</strong> stubborn shorts adding conviction. If the rally continues, it
            becomes fuel.
          </li>
          <li>
            <strong>Falling SI during a rally:</strong> shorts already covered. The rally is losing its squeeze engine —
            late to join.
          </li>
        </ul>
      </div>

      <h2>Putting it together</h2>
      <div className={styles.callout}>
        <strong>Same signal. Four trades.</strong>
        <br />
        <br />
        RSI(2) = 5 on AAPL at 10:30, trend-up regime, short float 8%, rising volume → <strong>conviction long</strong>.
        <br />
        RSI(2) = 5 on AAPL at 12:30, chop regime, short float 8%, declining volume → <strong>AVOID</strong>.
        <br />
        RSI(2) = 5 on AAPL at 15:45, any regime → <strong>AVOID</strong> (overnight gap risk).
        <br />
        RSI(2) = 5 on AAPL at 10:30, trend-down regime, short float 25%, positive catalyst →{' '}
        <strong>asymmetric long</strong> (betting on a squeeze off oversold, not on a trend reversal).
      </div>

      <p>
        The Screener and Strategist do this layering for you automatically. But knowing how the layers combine lets you
        understand <em>why</em> they told you AVOID on something that looks great on a single chart — and when to trust
        your own read over theirs.
      </p>
    </LearnLayout>
  );
};

export default ReadingSignals;
